import React, { useState } from 'react';
import SEOHead from '../components/SEOHead';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Alert,
  Snackbar,
  Paper,
} from '@mui/material';
import { motion } from 'framer-motion';
import SendIcon from '@mui/icons-material/Send';

const MotionBox = motion(Box);

const Contact = () => {
  const [formData, setFormData] = useState({
    from_name: '',
    from_email: '',
    subject: '',
    message: '',
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Replace this with your actual Lambda Function URL
    const LAMBDA_FUNCTION_URL = process.env.REACT_APP_LAMBDA_FUNCTION_URL;

    try {
      const response = await fetch(LAMBDA_FUNCTION_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSnackbar({
          open: true,
          message: 'Message sent successfully! We will get back to you shortly.',
          severity: 'success',
        });
        setFormData({ from_name: '', from_email: '', subject: '', message: '' });
      } else {
        throw new Error(result.message || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to send message. Please try again or email us directly at info@devhopeslabs.nl',
        severity: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <>
      <SEOHead
        title="Contact DevHopes Labs - Get Your Development Project Started"
        description="Get in touch with DevHopes Labs for your mobile and web development needs. Contact us for iOS app development, Android apps, React.js web applications, Shopify solutions, and custom software development. We're here to help bring your ideas to life."
        keywords="contact DevHopes Labs, mobile app development contact, web development contact, iOS app development consultation, Android app development quote, React.js development contact, Shopify development consultation, custom software development contact, Netherlands developers contact"
        url="/contact"
        type="website"
        schemaType="ContactPage"
      />

      <Box
        sx={{
          py: { xs: 8, md: 12 },
          mt: { xs: 7, md: 8 },
          bgcolor: 'background.default',
        }}
      >
        <Container maxWidth="md">
          <MotionBox
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '2rem', md: '2.5rem' },
                  mb: 2,
                  color: 'text.primary',
                }}
              >
                Get In Touch
              </Typography>
              <Typography 
                variant="h6" 
                color="text.secondary"
                sx={{ fontSize: { xs: '1rem', md: '1.1rem' } }}
              >
                Have a project in mind? Let's discuss how we can help you achieve your goals.
              </Typography>
            </Box>

            {/* Form Container */}
            <Paper
              elevation={0}
              sx={{
                p: { xs: 4, md: 6 },
                borderRadius: 3,
                boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
              }}
            >
              <form onSubmit={handleSubmit}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {/* Row 1: Name and Email */}
                  <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', sm: 'row' } }}>
                    <TextField
                      required
                      fullWidth
                      label="Name"
                      name="from_name"
                      value={formData.from_name}
                      onChange={handleChange}
                      variant="outlined"
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      required
                      fullWidth
                      label="Email"
                      name="from_email"
                      type="email"
                      value={formData.from_email}
                      onChange={handleChange}
                      variant="outlined"
                      sx={{ flex: 1 }}
                    />
                  </Box>

                  {/* Row 2: Subject */}
                  <TextField
                    required
                    fullWidth
                    label="Subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    variant="outlined"
                  />

                  {/* Row 3: Message */}
                  <TextField
                    required
                    fullWidth
                    multiline
                    rows={6}
                    label="Message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    variant="outlined"
                  />

                  {/* Submit Button */}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      size="large"
                      disabled={isSubmitting}
                      endIcon={<SendIcon />}
                      sx={{
                        py: 1.5,
                        px: 4,
                        fontWeight: 600,
                        borderRadius: '50px',
                        textTransform: 'none',
                        fontSize: '1rem',
                        boxShadow: '0 4px 15px rgba(20, 184, 166, 0.25)',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 8px 25px rgba(20, 184, 166, 0.35)',
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      {isSubmitting ? 'Sending...' : 'Send Message'}
                    </Button>
                  </Box>
                </Box>
              </form>
            </Paper>
          </MotionBox>
        </Container>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%', borderRadius: 2, boxShadow: 3 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Contact; 